import React, { useCallback, useEffect, useState } from 'react';
import DataTable from '../components/common/Table';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import RoleGuard from '../components/common/RoleGuard';
import MapView, { MapMarker } from '../components/MapView';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { toast } from '../components/common/Toast';
import ApartmentsFilterBar from '../components/apartments/ApartmentsFilterBar';
import BeaconSidePanel from '../components/apartments/BeaconSidePanel';
import BeaconManageModal from '../components/apartments/BeaconManageModal';
import { getList, create, update, remove, removePermanent } from '../api/apartments';
import { geocode, GeocodeResult } from '../api/maps';
import { usePermission } from '../hooks/usePermission';
import { useApartmentsFilter } from '../hooks/useApartmentsFilter';
import { Apartment } from '../types';
import './Pages.css';

type ViewMode = 'list' | 'map';

interface ApartmentForm {
  name: string;
  address: string;
  latitude: string;
  longitude: string;
  radius: string;
  speed_limit_kmh: string;
  warning_speed_kmh: string;
  zip_code: string;
  note: string;
}

const emptyForm: ApartmentForm = {
  name: '', address: '', latitude: '', longitude: '', radius: '300',
  speed_limit_kmh: '20', warning_speed_kmh: '30', zip_code: '', note: '',
};

const ApartmentsPage: React.FC = () => {
  const { canAccess } = usePermission();
  const canEdit = canAccess('apartments_manage');

  const { filter, setMode, setQuery, reset: resetFilter, apiParams } = useApartmentsFilter();

  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [modalOpen, setModalOpen] = useState(false);
  const [editApt, setEditApt] = useState<Apartment | null>(null);
  const [form, setForm] = useState<ApartmentForm>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<Apartment | null>(null);
  const [selectedApartmentId, setSelectedApartmentId] = useState<string | null>(null);
  const [beaconManageApt, setBeaconManageApt] = useState<Apartment | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GeocodeResult[]>([]);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getList({ page, ...apiParams });
      setApartments(res.items);
      setTotal(res.total);
    } catch { toast.error('장소 목록을 불러올 수 없습니다'); }
  }, [page, apiParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setPage(1); }, [apiParams]);

  const openCreate = () => {
    setEditApt(null);
    setForm(emptyForm);
    setSearchQuery('');
    setSearchResults([]);
    setModalOpen(true);
  };

  const openEdit = (apt: Apartment) => {
    setEditApt(apt);
    setForm({
      name: apt.name, address: apt.address,
      latitude: String(apt.latitude), longitude: String(apt.longitude),
      radius: String(apt.radius),
      speed_limit_kmh: String(apt.speed_limit_kmh), warning_speed_kmh: String(apt.warning_speed_kmh),
      zip_code: apt.zip_code || '', note: apt.note || '',
    });
    setSearchQuery(apt.address || '');
    setSearchResults([]);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        name: form.name, address: form.address,
        latitude: Number(form.latitude), longitude: Number(form.longitude),
        radius: Number(form.radius),
        speed_limit_kmh: Number(form.speed_limit_kmh), warning_speed_kmh: Number(form.warning_speed_kmh),
        zip_code: form.zip_code || undefined,
        note: form.note || undefined,
      };
      if (editApt) { await update(editApt.id, data); toast.success('장소가 수정되었습니다'); }
      else { await create(data); toast.success('장소가 등록되었습니다'); }
      setModalOpen(false); load();
    } catch { toast.error('저장에 실패했습니다'); }
  };

  const handleDelete = async (apt: Apartment) => {
    if (!window.confirm(`"${apt.name}"을(를) 비활성화하시겠습니까?`)) return;
    try { await remove(apt.id); toast.success('장소가 비활성화되었습니다'); load(); }
    catch { toast.error('비활성화에 실패했습니다'); }
  };

  const handleHardDelete = async () => {
    if (!deleteTarget) return;
    try {
      await removePermanent(deleteTarget.id);
      toast.success('장소가 영구 삭제되었습니다');
      setDeleteTarget(null);
      load();
    } catch { toast.error('장소 삭제에 실패했습니다'); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const results = await geocode(searchQuery.trim());
      setSearchResults(results);
      if (results.length > 0) {
        setForm((f) => ({
          ...f,
          address: results[0].address,
          latitude: String(results[0].latitude),
          longitude: String(results[0].longitude),
        }));
      } else {
        toast.error('검색 결과가 없습니다');
      }
    } catch {
      toast.error('주소 검색에 실패했습니다');
    } finally {
      setSearching(false);
    }
  };

  const selectSearchResult = (result: GeocodeResult) => {
    setForm((f) => ({
      ...f,
      address: result.address,
      latitude: String(result.latitude),
      longitude: String(result.longitude),
    }));
    setSearchResults([]);
    setSearchQuery(result.address);
  };

  const handlePinDrag = useCallback((coord: { lat: number; lng: number }) => {
    setForm((f) => ({
      ...f,
      latitude: String(coord.lat),
      longitude: String(coord.lng),
    }));
  }, []);

  const beaconSummary = (a: Apartment) => {
    const total = a.beacons?.length ?? 0;
    const lowCount = a.beacons?.filter(b => b.battery_low).length ?? 0;
    if (total === 0) return <span className="badge badge--gray">0개</span>;
    return lowCount > 0
      ? <span className="badge badge--red">{total}개 (부족 {lowCount})</span>
      : <span className="badge badge--green">{total}개 (정상)</span>;
  };

  const columns = [
    { key: 'name', label: '장소명' },
    { key: 'address', label: '주소' },
    { key: 'warning_speed_kmh', label: '경고속도', render: (a: Apartment) => `${a.warning_speed_kmh} km/h` },
    { key: 'speed_limit_kmh', label: '제한속도', render: (a: Apartment) => `${a.speed_limit_kmh} km/h` },
    { key: 'beacons', label: '비콘', render: (a: Apartment) => beaconSummary(a) },
    { key: 'actions', label: '', render: (a: Apartment) => (
      <div className="table-actions">
        <RoleGuard roles={['super_admin', 'admin', 'manager']}><button className="btn btn--sm btn--secondary" onClick={(e) => { e.stopPropagation(); openEdit(a); }}>수정</button></RoleGuard>
        <RoleGuard roles={['super_admin', 'admin', 'manager']}><button className="btn btn--sm btn--secondary" onClick={(e) => { e.stopPropagation(); setBeaconManageApt(a); }}>비콘</button></RoleGuard>
        <RoleGuard roles={['super_admin', 'admin']}><button className="btn btn--sm btn--danger" onClick={(e) => { e.stopPropagation(); handleDelete(a); }}>비활성화</button></RoleGuard>
        <RoleGuard roles={['super_admin', 'admin']}><button className="btn btn--sm btn--danger" onClick={(e) => { e.stopPropagation(); setDeleteTarget(a); }}>장소 삭제</button></RoleGuard>
      </div>
    )},
  ];

  const mapMarkers: MapMarker[] = apartments.filter(a => a.is_active).map((a) => ({
    id: a.id,
    lat: a.latitude,
    lng: a.longitude,
    radius: a.radius,
    label: a.name,
  }));

  const mapCenter = apartments.length > 0
    ? { lat: apartments[0].latitude, lng: apartments[0].longitude }
    : undefined;

  const modalCenter = form.latitude && form.longitude
    ? { lat: Number(form.latitude), lng: Number(form.longitude) }
    : undefined;

  return (
    <div>
      <div className="page-header">
        <h2>장소 관리</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="view-toggle" style={{ display: 'flex', gap: 0 }}>
            <button
              className={`btn btn--sm ${viewMode === 'list' ? 'btn--primary' : 'btn--secondary'}`}
              onClick={() => setViewMode('list')}
              style={{ borderRadius: '4px 0 0 4px' }}
            >
              목록
            </button>
            <button
              className={`btn btn--sm ${viewMode === 'map' ? 'btn--primary' : 'btn--secondary'}`}
              onClick={() => setViewMode('map')}
              style={{ borderRadius: '0 4px 4px 0' }}
            >
              지도
            </button>
          </div>
          <RoleGuard roles={['super_admin', 'admin', 'manager']}>
            <button className="btn btn--primary" onClick={openCreate}>장소 등록</button>
          </RoleGuard>
        </div>
      </div>

      <ApartmentsFilterBar
        filter={filter}
        onModeChange={setMode}
        onQueryChange={setQuery}
        onReset={resetFilter}
      />

      {viewMode === 'list' ? (
        <>
          <DataTable columns={columns} data={apartments} />
          <Pagination page={page} total={total} limit={20} onPageChange={setPage} />
        </>
      ) : (
        // ISS-063 — SDK throw 가 페이지 전체 흰 화면이 되는 것을 막는다.
        <ErrorBoundary
          fallback={
            <div style={{ width: '100%', height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: 8, border: '1px dashed #ccc' }}>
              <div style={{ textAlign: 'center', color: '#999' }}>
                <p style={{ margin: 0, fontSize: 14 }}>지도를 일시적으로 표시할 수 없습니다</p>
                <p style={{ margin: '4px 0 0', fontSize: 12 }}>목록 보기로 전환하거나 페이지를 새로고침해 주세요</p>
              </div>
            </div>
          }
        >
          <MapView markers={mapMarkers} center={mapCenter} zoom={12} height={600} onMarkerClick={(id) => setSelectedApartmentId(id)} />
        </ErrorBoundary>
      )}

      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="장소 삭제" onConfirm={handleHardDelete} confirmText="삭제" cancelText="취소">
        <p>현재 등록된 장소를 삭제합니다. 장소가 삭제되면 해당 장소와 관련된 과속 이력이 모두 삭제됩니다. 장소 삭제를 진행하시기 전에, 과속 이력 페이지에서 과속 이력 정보를 미리 백업해두시기를 권장드립니다.</p>
      </Modal>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editApt ? '장소 수정' : '장소 등록'} onConfirm={handleSubmit}>
        <div className="form-group"><label>장소명</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>

        {canEdit && (
          <div className="form-group">
            <label>주소 검색</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                placeholder="주소를 입력하고 검색하세요"
                style={{ flex: 1 }}
              />
              <button className="btn btn--sm btn--primary" onClick={handleSearch} disabled={searching}>
                {searching ? '...' : '검색'}
              </button>
            </div>
            {searchResults.length > 1 && (
              <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none', border: '1px solid #ddd', borderRadius: 4, maxHeight: 150, overflow: 'auto' }}>
                {searchResults.map((r, i) => (
                  <li
                    key={i}
                    onClick={() => selectSearchResult(r)}
                    style={{ padding: '6px 8px', cursor: 'pointer', borderBottom: i < searchResults.length - 1 ? '1px solid #eee' : 'none', fontSize: 13 }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f0f0f0')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    {r.address}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="form-group"><label>주소</label><input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
        <div className="form-group"><label>우편번호</label><input value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value})} /></div>

        {modalCenter && (
          <div style={{ margin: '8px 0' }}>
            {/* ISS-063 — 모달 안 MapView 도 SDK throw 시 모달 자체가 흰 화면 되는 것 방지 */}
            <ErrorBoundary
              fallback={
                <div style={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: 8, border: '1px dashed #ccc' }}>
                  <p style={{ margin: 0, fontSize: 12, color: '#999' }}>지도 미리보기를 표시할 수 없습니다</p>
                </div>
              }
            >
              <MapView
                markers={[]}
                center={modalCenter}
                zoom={16}
                height={300}
                editablePin={{ lat: modalCenter.lat, lng: modalCenter.lng, radius: Number(form.radius) || 300 }}
                onPinDrag={handlePinDrag}
              />
            </ErrorBoundary>
            <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>핀을 드래그하여 위치를 미세 조정할 수 있습니다</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <div className="form-group" style={{ flex: 1 }}><label>위도</label><input type="number" step="any" value={form.latitude} onChange={e => setForm({...form, latitude: e.target.value})} /></div>
          <div className="form-group" style={{ flex: 1 }}><label>경도</label><input type="number" step="any" value={form.longitude} onChange={e => setForm({...form, longitude: e.target.value})} /></div>
        </div>
        <div className="form-group"><label>반경 (m)</label><input type="number" value={form.radius} onChange={e => setForm({...form, radius: e.target.value})} /></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="form-group" style={{ flex: 1 }}><label>경고속도 (km/h)</label><input type="number" value={form.warning_speed_kmh} onChange={e => setForm({...form, warning_speed_kmh: e.target.value})} /></div>
          <div className="form-group" style={{ flex: 1 }}><label>제한속도 (km/h)</label><input type="number" value={form.speed_limit_kmh} onChange={e => setForm({...form, speed_limit_kmh: e.target.value})} /></div>
        </div>
        <div className="form-group">
          <label>메모 (비공개)</label>
          <textarea
            value={form.note}
            onChange={e => setForm({...form, note: e.target.value})}
            maxLength={500}
            rows={3}
            style={{ width: '100%' }}
            placeholder="관리용 메모 (서비스에 노출되지 않음)"
          />
          <span style={{ fontSize: 11, color: '#999' }}>{form.note.length}/500</span>
        </div>
      </Modal>

      <BeaconSidePanel
        isOpen={selectedApartmentId !== null}
        apartmentId={selectedApartmentId}
        apartmentName={apartments.find(a => a.id === selectedApartmentId)?.name ?? ''}
        onClose={() => setSelectedApartmentId(null)}
      />

      {beaconManageApt && (
        <BeaconManageModal
          isOpen={!!beaconManageApt}
          onClose={() => setBeaconManageApt(null)}
          apartmentId={beaconManageApt.id}
          onUpdate={load}
        />
      )}
    </div>
  );
};

export default ApartmentsPage;
